// Builds the public (cross-origin) `donationCompleted` payload sent to the
// embedding page via sendPublic. Shared by v6 and v7 so the wire shape stays
// identical across widget versions. Same-origin embedders still get the full
// donation object; this is only the public slice.
//
// The partner-facing contract intentionally exposes the *class* of donation,
// not the payment method — `currencyType` tells a listener how to read the
// rest (fiat: amount + ISO currency; crypto: amount + ticker; other: neither),
// without divulging card vs. PayPal vs. wallet etc.

// The public currencyType. `method` may be a specific method (v7) or already a
// class (v6 collapses card/bank/PayPal/Venmo to 'fiat') — either works:
//   - no amount, or no currency to denominate it → 'other'. Today that's stock
//     and only stock: nothing settles until the DonateStock webhook fires, so
//     there's no amount at completion. Both are required because both are what
//     make a monetary value readable — an amount with no currency is the exact
//     ambiguity this field was added to remove, so we report no value rather
//     than one a listener would have to guess at. Checked FIRST, so no method
//     can claim an amount-bearing shape it has no data to fill.
//   - crypto → 'crypto': a native crypto amount + ticker (0.01 / BTC). Taken
//     from the payload's own `crypto` flag rather than the method — every
//     settled order carries it (FundraiserLandingPages::OrderSerializer's
//     `crypto?` attribute, which AMS 0.9 serializes as `crypto`, true or false),
//     with the method as fallback. Preferring the payload means a stale or
//     mismatched method can't publish a crypto ticker under `currencyType:
//     'fiat'` — the one combination this field exists to prevent.
//   - everything else → 'fiat': card, bank, PayPal, Venmo, wallet (Apple /
//     Google Pay), EE wallet, deferred wire/check/pledge, and DAF — all report a
//     fiat amount + ISO code, on BOTH widget versions.
//
// Note stock reaches 'other' by having no amount rather than by name. If a
// future integration does report a settled amount at completion, it would be
// described as 'fiat' — which is what the contract means by fiat (an amount
// plus an ISO currency), so that degrades correctly rather than lying.
// Takes the already-resolved values rather than the raw detail, so the type can
// never disagree with what actually gets reported (see buildCompletedDonationPayload).
const currencyTypeFor = ({ amount, currency, crypto }, method) =>
  amount == null || currency == null ? 'other'
  : (crypto ?? method == 'crypto') ? 'crypto'
  : 'fiat'

// Normalize the donation's currency to an uppercase code. On every settled path
// it arrives as the order serializer's object — `{ symbol, iso_code }` for fiat,
// `{ iso_code, exponent, format }` for crypto — so `iso_code` is the usual read.
// The string branch covers a bare currency id ('usd') straight off donation
// state, for any path that lands here without a serialized order. Returns
// undefined when the source omits it entirely.
//
// NOT always ISO 4217. On the crypto branch this is the Coinbase ticker, which
// can be 4 characters (DOGE, SHIB, USDC) or even hyphenated — 'USDC-S', the
// Solana USDC entry (lib/coinbase_api.rb). So don't add a 3-letter assumption
// here, and note that a consumer passing this straight to
// `Intl.NumberFormat(locale, { style: 'currency', currency })` throws
// RangeError on those. That's exactly what `currencyType` is for: branch on it
// before formatting. (The rich object's key is literally named `iso_code` even
// when it holds a ticker — see FundraiserLandingPages::OrderSerializer.)
const currencyCode = currency => {
  const code = typeof currency == 'string' ? currency : currency?.iso_code
  return code ? code.toUpperCase() : undefined
}

// Two independent method traits. Neither implies the other:
//   pending       — the money hasn't settled by the time we notify the page. ACH
//                   clears asynchronously and can still fail; wire / check /
//                   pledge, DAF and stock settle off-platform later. Crypto is
//                   NOT pending — we don't notify until the transfer has been
//                   observed on-chain.
//   principalOnly — the method can't carry a tip or covered fees at all, so any
//                   figure the form computed for them is fiction here.
// ACH is pending but fee-capable; crypto is principal-only but settled — hence
// two flags rather than one.
//
// Keyed by BOTH widget vocabularies, since this module serves v6 and v7 (see
// traitsFor). Deliberately local rather than in src/payments/paymentMethods.js:
// that registry documents itself as v7-only ("v6 is deliberately NOT a
// consumer"), and folding v6's panel pseudo-names into it would undo that
// decision. Promote to a shared module if the record-saving layer ever reads the
// same traits — it makes the same principal-only distinction by hand today.
const METHOD_TRAITS = {
  // Settled by the time we notify, and fee/tip-capable.
  apple_pay:      {},
  google_pay:     {},
  paymentRequest: {},                                  // v6's Apple/Google Pay tab
  credit_card:    {},
  paypal:         {},
  venmo:          {},
  wallet:         {},                                  // v6 panel: EE wallet balance
  // Settles later, but still fee/tip-capable.
  bank:           { pending: true },
  // Settles later, and never tipped or fee-covered.
  wire:           { pending: true, principalOnly: true },
  check:          { pending: true, principalOnly: true },
  pledge:         { pending: true, principalOnly: true },
  deferred:       { pending: true, principalOnly: true },  // v6 panel: wire/check/pledge
  stock:          { pending: true, principalOnly: true },
  daf:            { pending: true, principalOnly: true },
  // Settled on-chain before we notify, but never tipped or fee-covered.
  crypto:         { principalOnly: true },
}

// v6 collapses card / ACH / PayPal / Venmo into a single `fiat` panel, so the
// panel name alone can't tell an ACH donation from a card one — but v6 puts the
// specific choice on the detail as `paymentOption` (donate_v6/fiat/App.jsx).
// Prefer that, and fall back to whatever the caller passed (v7 always passes a
// specific method; v6's non-fiat panels are already specific enough).
//
// Note `fiat` is deliberately absent from METHOD_TRAITS. If it ever reaches us
// it means a v6 fiat path stopped setting paymentOption, and reporting an ACH
// donation as settled is worse than shouting about it.
//
// Reading the caller's method (rather than `detail.paymentMethod`) also keeps
// Stripe's runtime express types out of here: usePayment stamps
// `paymentMethodKey: expressPaymentType`, which can be `link` as well as
// apple_pay / google_pay. ExpressCheckout only renders under isWalletMethod, so
// the prop is always a mapped key. Every express type is settled and fee-capable,
// so `pending` would be right either way — but switching to the stamped key would
// start tripping the log above.
const traitsFor = (detail, method) => {
  const key = detail.paymentOption ?? method
  const traits = METHOD_TRAITS[key]
  if (!traits && key != null)
    console.error(`completedDonation: unrecognized payment method '${key}'`)
  return traits || {}
}

// Amounts are reported in the donation's own currency — we never convert, since
// the USD value isn't known at finalization (and sending it would oblige us to
// send a USD tip/fees/total too).
//
// Each currencyType has a GUARANTEED shape, so a listener can read the payload
// without null/undefined guards:
//   every type → currencyType, pending  (pending: the money hasn't settled yet —
//                ACH, wire/check/pledge, DAF, stock. See METHOD_TRAITS.)
//   fiat       → + currency, amount, fees, tip, total  (fees null unless the
//                donor actually covered them; tip null unless they tipped)
//   crypto     → + currency (the ticker — NOT ISO 4217; see currencyCode), amount
//   other      → nothing further
//
// Each key is assigned unconditionally for its type, so every donation of that
// type has the same shape — nothing is conditionally omitted. `amount` is
// guaranteed non-null for fiat and crypto (an absent one becomes 'other'
// upstream), so `total`'s fallback to `amount` always resolves.
//
// What the money keys MEAN, since the names are less self-describing than they
// look:
//   `amount` is the donor's gift — never includes covered fees. How it resolves,
//            and why it can't just be the serializer's `amount`, is documented
//            at the resolution itself in the builder below.
//   `total`  is what was actually charged.
//   `fees`   is what the donor ACTUALLY paid to cover costs, or null. The
//            widget's own `fees` is a quote computed from whether the partner
//            offers cover-costs, never from whether this donor accepted
//            (useDonation.js), so we gate it on `cover_fees` — otherwise a
//            declined donation reports a fee nobody was charged, and summing the
//            key books revenue that was never taken. It's also the grossed-up
//            figure (fee on the larger charge), so it isn't even the fee the
//            processor took when declined.
// So the arithmetic always closes: total == amount + (fees ?? 0) + (tip ?? 0).
//
// `fees`/`tip` coalesce to **null rather than being dropped**, which is what
// keeps this additive: standard fiat already set them to a literal `null` when
// cover-costs / tipping is off (useDonation.js, both versions) and those keys
// were on the pre-existing wire, so omitting them would break a live partner
// payload (`'fees' in data`, `total - fees` → NaN). Don't "simplify" that away.
// That plus `total ?? amount` is what harmonizes the serializer-sourced fiat
// methods (v7 DAF today, any future one) with the useDonation-sourced ones,
// which already carry all four keys.
//
// `currency` can't go missing from a fiat or crypto payload: a source without
// one classifies as 'other' (see currencyTypeFor), so every payload carrying an
// `amount` carries the currency it's denominated in. No null-check needed on the
// receiving end — which is the whole point of the field.
export const buildCompletedDonationPayload = (detail, paymentMethod) => {
  const traits = traitsFor(detail, paymentMethod)
  // `currency` and `amount` are resolved before the type is classified, because
  // they decide the type as well as populate it — classifying on the resolved
  // values means the type can't promise a key the payload then fails to carry.
  const currency = currencyCode(detail.currency)
  // The donor's gift — what they chose to give — never including fees they opted
  // to cover. `useDonation` computes it (both versions) and it rides the
  // completed donation as `giftAmount`, already scaled to the currency's
  // exponent like `tip` and `total`.
  //
  // It can't just be `detail.amount`: the serializer's `amount` is the sum of
  // the donation order items, which DOES include covered fees, so reporting it
  // made `amount + fees` double-count and `fees` look like revenue never taken.
  // (The 2018 original had this right — components/donate/cover_costs/
  // Mappings.js kept `amount` as the gift and moved acceptance into `total`,
  // asserting it in the Apple Pay line items where the gift plus an "(Included
  // Cover Costs)" line had to sum to the total. It regressed in b43e0d5cb0,
  // 2026-02-18, when an unrelated drafts refactor began spreading the
  // serialized order flat over widget state.)
  //
  // The `?? detail.amount` fallback covers the paths that carry no
  // `giftAmount`, and it's exact on all of them because none can add a fee to
  // the gift:
  //   - crypto — `amount` is the native crypto amount, which is the gift.
  //   - stock — reports no amount at all, so this classifies as `other` anyway.
  //   - DAF — principal-only, `cover_fees` false.
  //   - v6 EE wallet — spreads neither state nor rest, but moving funds between
  //     platform wallets has no processing cost, so cover-costs is never offered
  //     and `amount` is the gift. That's the one path where correctness rests on
  //     a product fact rather than the code, hence writing it down.
  const amount = detail.giftAmount ?? detail.amount
  const currencyType = currencyTypeFor(
    { amount, currency, crypto: detail.crypto }, paymentMethod,
  )
  // Same principle as the unrecognized-method log below: a partner silently
  // losing `amount` is harder to diagnose than console noise. Fires only on
  // PARTIAL money data — a detail reporting an amount we can't denominate.
  // Stock reports no amount at all, which is the honest case, so it stays quiet
  // (its finisher carries neither `amount` nor `giftAmount`).
  //
  // Tests the resolved `amount`, not `detail.amount`, so it covers a gift that
  // arrived only as `giftAmount`. No reachable path does that today — the three
  // finishers spreading donation state are all fiat and carry `currency` on the
  // same spread as `giftAmount`, and useDonation's fallback chain always
  // resolves a currency object — but the check belongs on the value being
  // dropped rather than on one of the two places it can come from.
  if (currencyType == 'other' && amount != null)
    console.error(
      'completedDonation: amount present but unreportable, degrading to `other`',
      { currency: detail.currency, cover_fees: detail.cover_fees },
    )

  const payload = { currencyType, pending: !!traits.pending }

  if (currencyType != 'other') {
    payload.currency = currency
    payload.amount = amount
  }

  if (currencyType == 'fiat') {
    // `fees` only when the donor actually accepted. `cover_fees` is the real
    // acceptance flag, and it's false on every principal-only method, so this
    // covers those too.
    //
    // What this is actually for: `cover_fees` false with a non-null quote. The
    // donor declined cover-costs, the form computed the quote anyway, and it
    // must not reach the wire as a fee they paid. Reachable on every fiat
    // method, and separately tested.
    //
    // The zero-collapse on top of it is defence-in-depth. Both routes to a zero
    // were traced and neither reaches completion today:
    //   - No live fee config has a zero `rate` — all three bundles hardcode
    //     constants (card 0.029 + 0.30, ACH 0.008, PayPal 0.029); only
    //     `internalRate` is partner-variable, and that's one addend. So a real
    //     config yields '0.00' only at amount 0 (and only for ACH, which has no
    //     flat base), which is the Start panel before the donor types.
    //   - No fee-capable method reaches completion without a config. Each
    //     bundle's presence is gated on the same @payment_methods entry that
    //     puts its row on the picker (_config.erb: creditCardProps, bankProps,
    //     paypalProps), so a method can't be offered with its config missing;
    //     every other switch branch is principalOnly. Apple/Google Pay have a
    //     second gate — discovery itself runs only when the card bundle exists
    //     (App.jsx: `useExpressCheckoutDiscovery({ enabled: !!creditCardProps })`),
    //     so a falsy bundle means those rows are never even added.
    // Kept because it costs nothing and it holds if a config ever gains a zero
    // rate or a bundle loses its feeConfig, either of which would otherwise
    // report a fee nobody paid. Note FTF (`pledgeCoversFees`) is NOT such a
    // case — it only selects tip copy and a disclaimer, never the fee config.
    //
    // `Number()` rather than truthiness so the spelling doesn't matter: calcFees
    // returns a bare `0` with no config and a scaled string otherwise, and a
    // truthiness check would pass '0.00' straight through.
    payload.fees = detail.cover_fees && Number(detail.fees) ? detail.fees : null
    // `tip` and `total` come from the form's state, which on a principal-only
    // method still reflects what the form OFFERED — a cover-costs form defaults
    // state.coverCosts to true and never shows the donor a checkbox on these
    // steps, so `total` arrives pre-inflated by a fee nobody was charged. The
    // gift is the whole charge on these methods.
    payload.tip = traits.principalOnly ? null : detail.tip ?? null
    payload.total = traits.principalOnly ? amount : detail.total ?? amount
  }

  return payload
}
